import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Checkbox,
  FormGroup,
  Grid,
  Alert,
  CircularProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOffline } from '../../../hooks/useOffline';
import OfflineBanner from '../../../components/common/OfflineBanner';
import WorkoutStructureBuilder from '../../../features/workouts/components/WorkoutStructureBuilder';
import {
  useGetTrainerAthletesQuery,
  useListWorkoutCoursesQuery,
  useGetWorkoutCourseQuery,
  useCreateWorkoutMutation,
} from '../../../store/api/baseApi';
import type { 
  WorkoutPriority,
  CourseStep 
} from '../../../store/api/models/workoutModels';

interface AthleteOption {
  id: number;
  name: string;
  email: string;
}

interface AssignWorkoutFormData {
  athleteId: string;
  templateId: string | null;
  scheduledDate: Date;
  priority: WorkoutPriority;
  notes: string;
  repeatType: 'single' | 'weekly';
  repeatDays: string[];
  workoutStructure: CourseStep[];
}

const validationSchema: yup.ObjectSchema<AssignWorkoutFormData> = yup.object().shape({
  athleteId: yup.string().required('Please select an athlete'),
  templateId: yup.string().nullable().defined(),
  scheduledDate: yup.date().required('Please select a date').min(new Date(), 'Date must be in the future'),
  priority: yup.string().oneOf(['low', 'medium', 'high'] as const).required('Please select priority'),
  notes: yup.string().max(500, 'Notes must be less than 500 characters').default(''),
  repeatType: yup.string().oneOf(['single', 'weekly'] as const).required(),
  repeatDays: yup.array().of(yup.string().required()).default([]).when('repeatType', {
    is: 'weekly',
    then: (schema) => schema.min(1, 'Select at least one day for weekly repeat'),
    otherwise: (schema) => schema,
  }),
  workoutStructure: yup.array().of(yup.object().required()).min(1, 'Workout must have at least one step').default([]),
}) as yup.ObjectSchema<AssignWorkoutFormData>;

const AssignWorkoutPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isOffline } = useOffline();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [workoutStructure, setWorkoutStructure] = useState<CourseStep[]>([]);

  const templateIdFromUrl = searchParams.get('templateId');

  // Fetch athletes
  const { data: athletesResponse, isLoading: isLoadingAthletes } = useGetTrainerAthletesQuery({
    consentStatus: 'approved',
  });

  // Fetch templates
  const { data: templates = [], isLoading: isLoadingTemplates } = useListWorkoutCoursesQuery({
    skip: 0,
    limit: 1000,
  });

  // Fetch selected template if templateId is provided
  const { data: selectedTemplate } = useGetWorkoutCourseQuery(
    Number(templateIdFromUrl),
    { skip: !templateIdFromUrl }
  );

  const [createWorkout] = useCreateWorkoutMutation();

  const athletes: AthleteOption[] = athletesResponse?.athletes?.map((athlete: any) => ({
    id: athlete.id,
    name: athlete.name || athlete.athlete_name || 'Unknown',
    email: athlete.email || athlete.athlete_email || '',
  })) || [];

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<AssignWorkoutFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      athleteId: '',
      templateId: templateIdFromUrl || null,
      scheduledDate: new Date(),
      priority: 'medium' as WorkoutPriority,
      notes: '',
      repeatType: 'single',
      repeatDays: [],
      workoutStructure: [],
    },
  });

  const repeatType = watch('repeatType');
  const selectedTemplateId = watch('templateId');

  // Load selected template structure
  useEffect(() => {
    if (selectedTemplate) {
      setWorkoutStructure(selectedTemplate.structure);
      setValue('workoutStructure', selectedTemplate.structure);
    }
  }, [selectedTemplate, setValue]);

  const handleTemplateSelect = (templateId: string | null) => {
    if (templateId) {
      const template = templates.find((t) => t.id === Number(templateId));
      if (template) {
        setWorkoutStructure(template.structure);
        setValue('workoutStructure', template.structure);
      }
    } else {
      setWorkoutStructure([]);
      setValue('workoutStructure', []);
    }
  };

  const handleStructureChange = (structure: CourseStep[]) => {
    setWorkoutStructure(structure);
    setValue('workoutStructure', structure);
  };

  const onSubmit = async (data: AssignWorkoutFormData) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const workoutData = {
        course_id: data.templateId ? Number(data.templateId) : undefined,
        assigned_to_id: Number(data.athleteId),
        scheduled_at: data.scheduledDate.toISOString(),
        priority: data.priority,
        assignment_notes: data.notes,
      };

      if (data.repeatType === 'weekly' && data.repeatDays.length > 0) {
        // Create multiple workouts for weekly pattern
        const promises = data.repeatDays.map((day) => {
          const dayIndex = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].indexOf(day);
          const nextDate = new Date(data.scheduledDate);
          const currentDay = nextDate.getDay();
          const daysToAdd = (dayIndex - currentDay + 7) % 7 || 7;
          nextDate.setDate(nextDate.getDate() + daysToAdd);

          return createWorkout({
            ...workoutData,
            scheduled_at: nextDate.toISOString(),
          }).unwrap();
        });

        await Promise.all(promises);
      } else {
        // Create single workout
        await createWorkout(workoutData).unwrap();
      }

      if (isOffline) {
        console.log('Queuing workout assignment for offline sync:', data);
      }

      navigate('/trainer/dashboard');
    } catch (error: any) {
      setSubmitError(
        error?.data?.detail ||
        error?.data?.message ||
        'Failed to assign workout. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const weekDays = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' },
  ];

  const isLoading = isLoadingAthletes || isLoadingTemplates;

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <OfflineBanner />

        <Paper elevation={2} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Assign Workout
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Create and assign a workout to one of your athletes
          </Typography>

          {submitError && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSubmitError(null)}>
              {submitError}
            </Alert>
          )}

          {isOffline && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              You are offline. This workout will be queued and assigned when you reconnect.
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <Grid container spacing={3}>
              {/* Athlete Selection */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="athleteId"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      options={athletes}
                      getOptionLabel={(option) =>
                        typeof option === 'string'
                          ? athletes.find((a) => a.id === Number(option))?.name || ''
                          : option.name
                      }
                      onChange={(_, value) => field.onChange(value?.id?.toString() || '')}
                      value={athletes.find((a) => a.id === Number(field.value)) || null}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Athlete"
                          error={!!errors.athleteId}
                          helperText={errors.athleteId?.message}
                          required
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          <Box>
                            <Typography variant="body1">{option.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.email}
                            </Typography>
                          </Box>
                        </li>
                      )}
                    />
                  )}
                />
              </Grid>

              {/* Template Selection (Optional) */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="templateId"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      options={templates}
                      getOptionLabel={(option) =>
                        typeof option === 'string'
                          ? templates.find((t) => t.id === Number(option))?.name || ''
                          : option.name
                      }
                      onChange={(_, value) => {
                        const id = value?.id?.toString() || null;
                        field.onChange(id);
                        handleTemplateSelect(id);
                      }}
                      value={templates.find((t) => t.id === Number(field.value)) || null}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Workout Template (Optional)"
                          helperText="Select a template or build a custom workout below"
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          <Box>
                            <Typography variant="body1">{option.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.description}
                            </Typography>
                          </Box>
                        </li>
                      )}
                    />
                  )}
                />
              </Grid>

              {/* Scheduled Date */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="scheduledDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      label="Scheduled Date"
                      minDate={new Date()}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          error: !!errors.scheduledDate,
                          helperText: errors.scheduledDate?.message,
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Priority */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="priority"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth required error={!!errors.priority}>
                      <InputLabel>Priority</InputLabel>
                      <Select {...field} label="Priority">
                        <MenuItem value="low">Low</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Repeat Options */}
              <Grid item xs={12}>
                <Controller
                  name="repeatType"
                  control={control}
                  render={({ field }) => (
                    <FormControl component="fieldset">
                      <FormLabel component="legend">Repeat Options</FormLabel>
                      <RadioGroup {...field} row>
                        <FormControlLabel value="single" control={<Radio />} label="Single Workout" />
                        <FormControlLabel value="weekly" control={<Radio />} label="Weekly Pattern" />
                      </RadioGroup>
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Weekly Repeat Days */}
              {repeatType === 'weekly' && (
                <Grid item xs={12}>
                  <Controller
                    name="repeatDays"
                    control={control}
                    render={({ field }) => (
                      <FormControl component="fieldset" error={!!errors.repeatDays}>
                        <FormLabel component="legend">Repeat on Days</FormLabel>
                        <FormGroup row>
                          {weekDays.map((day) => (
                            <FormControlLabel
                              key={day.value}
                              control={
                                <Checkbox
                                  checked={field.value.includes(day.value)}
                                  onChange={(e) => {
                                    const newValue = e.target.checked
                                      ? [...field.value, day.value]
                                      : field.value.filter((d) => d !== day.value);
                                    field.onChange(newValue);
                                  }}
                                />
                              }
                              label={day.label}
                            />
                          ))}
                        </FormGroup>
                        {errors.repeatDays && (
                          <Typography variant="caption" color="error">
                            {errors.repeatDays.message}
                          </Typography>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>
              )}

              {/* Notes */}
              <Grid item xs={12}>
                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Notes"
                      multiline
                      rows={3}
                      fullWidth
                      error={!!errors.notes}
                      helperText={errors.notes?.message || 'Add any additional instructions or notes for the athlete'}
                    />
                  )}
                />
              </Grid>

              {/* Workout Structure Builder */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Workout Structure
                </Typography>
                <WorkoutStructureBuilder
                  initialStructure={workoutStructure}
                  onChange={handleStructureChange}
                />
                {errors.workoutStructure && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    {errors.workoutStructure.message}
                  </Typography>
                )}
              </Grid>

              {/* Action Buttons */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate(-1)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                    startIcon={isSubmitting && <CircularProgress size={20} />}
                  >
                    {isSubmitting ? 'Assigning...' : 'Assign Workout'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>
    </LocalizationProvider>
  );
};

export default AssignWorkoutPage;